import { useEffect, useRef, useState } from 'react'
import lottie from 'lottie-web/build/player/lottie_light'

export type StepAnimationName =
  | 'account'
  | 'add-on'
  | 'bundle'
  | 'fail'
  | 'purchase'
  | 'success'

export function StepAnimation({
  name,
  swapTo,
}: {
  name: StepAnimationName
  swapTo?: StepAnimationName
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [currentName, setCurrentName] = useState(name)
  const [loaded, setLoaded] = useState(false)
  const [transition, setTransition] = useState<'in' | 'out' | null>(null)

  useEffect(() => {
    setCurrentName(name)
    setTransition(null)
  }, [name])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    setLoaded(false)
    container.replaceChildren()

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    let swapTimer: number | undefined

    const animation = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: `${import.meta.env.BASE_URL}animations/${currentName}.json`,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid meet',
      },
    })

    const handleLoaded = () => {
      setLoaded(true)
      if (reduceMotion) {
        if (swapTo && currentName === name) {
          setCurrentName(swapTo)
          return
        }
        animation.goToAndStop(animation.totalFrames - 1, true)
        return
      }
      animation.play()
    }

    const handleComplete = () => {
      if (!swapTo || currentName !== name) return
      setTransition('out')
      swapTimer = window.setTimeout(() => {
        setCurrentName(swapTo)
        setTransition('in')
      }, 340)
    }

    animation.addEventListener('DOMLoaded', handleLoaded)
    animation.addEventListener('complete', handleComplete)

    return () => {
      if (swapTimer !== undefined) window.clearTimeout(swapTimer)
      animation.removeEventListener('DOMLoaded', handleLoaded)
      animation.removeEventListener('complete', handleComplete)
      animation.destroy()
    }
  }, [currentName, name, swapTo])

  const fallback = currentName === 'success'
    ? '✓'
    : currentName === 'fail'
      ? '×'
      : ''

  return (
    <div
      className={`step-animation step-animation--${currentName}${transition ? ` animation-${transition}` : ''}`}
      aria-hidden="true"
    >
      <div ref={containerRef} className="step-animation-canvas" />
      {!loaded && fallback && (
        <span className="step-animation-fallback">{fallback}</span>
      )}
    </div>
  )
}
